import { useEditorStore } from "./editorStore";

export const EDITOR_USER_ID_STORAGE_KEY = "print_editor_user_id";

function randomHex(bytes = 16) {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const arr = new Uint8Array(bytes);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  return `${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
}

export function persistEditorAuthToken(token: string | null) {
  useEditorStore.getState().setAuthToken(token);
}

export function getEditorAuthToken(): string | null {
  return useEditorStore.getState().authToken;
}

export function getOrCreateEditorUserId(): string {
  if (typeof window === "undefined") {
    return "anon:server";
  }

  const existing = window.localStorage.getItem(EDITOR_USER_ID_STORAGE_KEY);
  if (existing) return existing;

  const userId = `anon:${randomHex(16)}`;
  window.localStorage.setItem(EDITOR_USER_ID_STORAGE_KEY, userId);
  return userId;
}

function getProjectClaimStorageKey(projectPublicId: string) {
  return `printapp_claim_${projectPublicId}`;
}

export function persistProjectClaimToken(projectPublicId: string, claimToken: string | null) {
  if (typeof window === "undefined" || !projectPublicId) return;

  const storageKey = getProjectClaimStorageKey(projectPublicId);
  if (claimToken) {
    window.localStorage.setItem(storageKey, claimToken);
  } else {
    window.localStorage.removeItem(storageKey);
  }
}

export function getProjectClaimToken(projectPublicId?: string | null): string | null {
  if (typeof window === "undefined" || !projectPublicId) return null;
  return window.localStorage.getItem(getProjectClaimStorageKey(projectPublicId));
}

export function buildPrintApiHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  const token = getEditorAuthToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

export async function printApiGet<T>(path: string): Promise<T> {
  const res = await fetch(`/api/print/${path}`, {
    cache: "no-store",
    headers: buildPrintApiHeaders()
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

export async function printApiPost<T>(path: string, body: any): Promise<T> {
  const res = await fetch(`/api/print/${path}`, {
    method: "POST",
    headers: buildPrintApiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`POST ${path} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

export async function printApiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`/api/print/${path}`, {
    method: "DELETE",
    headers: buildPrintApiHeaders()
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`DELETE ${path} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

export async function issueProjectSession(input: {
  project_id?: number | null;
  project_public_id?: string | null;
  role?: string;
}) {
  const claimToken = getProjectClaimToken(input.project_public_id);
  const body: Record<string, unknown> = {
    user_id: getOrCreateEditorUserId(),
    role: input.role || "user"
  };

  if (input.project_id) body.project_id = input.project_id;
  if (input.project_public_id) body.project_public_id = input.project_public_id;
  if (claimToken) body.claim_token = claimToken;

  const res = await fetch("/api/print/auth/issue-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`POST auth/issue-session failed: ${res.status} ${txt}`);
  }

  const json = await res.json();
  persistEditorAuthToken(json.token || null);
  return json;
}

export async function printApiPut<T>(path: string, body: any): Promise<T> {
  const res = await fetch(`/api/print/${path}`, {
    method: "PUT",
    headers: buildPrintApiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`PUT ${path} failed: ${res.status} ${txt}`);
  }
  return res.json();
}
